import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  CalendarRange,
  Heart,
  Compass,
  Loader2,
  Lock,
  Hourglass,
  Mic,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { streamAssistant, transcribeAudio } from "@/lib/aiClient";
import { looksLargeRequest, genBatchInfo } from "@/lib/largeRequest";
import { getAllEvents } from "@/lib/storage";
import { getAllTasks } from "@/lib/tasks";
import { getPlanningMemory, getRecentDecisions, recordDecisions } from "@/lib/planningMemory";
import { todayISO } from "@/lib/time";
import {
  getPendingProposals,
  removeProposalBatch,
  extractProposals,
  extractMemorySuggestions,
  stripStreamingArtifacts,
  scrubProposalArtifacts,
  claimsProposalSuccess,
} from "@/lib/proposals";
import { extractBulkPayload, buildBulkProposal } from "@/lib/bulkProposals";
import { repairProposalWeekdays } from "@/lib/recurrence";
import { runPipeline } from "@/lib/proposalPipeline";
import { getActivePackage, setActivePackage, clearActivePackage } from "@/lib/proposalPackages";
import {
  getPendingRequest, markReadyForReview, markFailedPartial, markApplied,
  clearPendingRequest, packageSignature, REQ_STATUS,
} from "@/lib/pendingRequest";
import {
  createBulkJob, runBulkJob, getBulkJobs, cancelBulkJob, rollbackBulkJob,
  retryFailedItems, resumeActiveJobs, removeBulkJob,
} from "@/lib/bulkJob";
import ProposalCard from "@/components/ProposalCard";
import ProposalReview from "@/components/ProposalReview";
import BulkProposalCard from "@/components/BulkProposalCard";
import BulkJobProgress from "@/components/BulkJobProgress";
import MemorySuggestionCard from "@/components/MemorySuggestionCard";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";
import { aiUnlocked, aiAccess, isUnlimited, MODE_LEVEL, planOf } from "@/lib/access";
import { normalizeMode, resolveInitialMode } from "@/lib/assistantMode";
import PlanComparison from "@/components/PlanComparison";

const MODE_META = [
  { id: "assistant", icon: CalendarRange },
  { id: "strategy", icon: Compass },
  { id: "support", icon: Heart },
];

// A few of the most useful Advisor prompts, surfaced as an always-reachable
// toolbar once a conversation has started (indexes into assistant.advisorChips).
const ADVISOR_QUICK_CHIP_INDEXES = [0, 1, 2, 4, 8];

function makeSessionId() {
  return (
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `s-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  );
}

// Monotonic, deterministic message id (chat is append-only, never reordered).
let MSG_SEQ = 0;
function nextMsgId() {
  MSG_SEQ += 1;
  return `msg-${MSG_SEQ}`;
}

// --- Completeness helpers (Option B: guarantee a COMPLETE authoritative draft
// before Proposal Review). The AI declares an "expected" total; if it emitted
// fewer operations we silently continue generation to fill the gap. -----------
const MAX_COMPLETION_TRIES = 3;
const proposalKey = (p) => {
  const n = p.new || {};
  const o = p.old || {};
  return [
    p.kind, p.event_id || "",
    String(n.title || o.title || "").trim().toLowerCase(),
    n.date || o.date || "",
    n.startHour ?? o.startHour ?? "", n.startMinute ?? o.startMinute ?? "",
  ].join("|");
};
function mergeProposals(base, extra) {
  const seen = new Set(base.map(proposalKey));
  const out = base.slice();
  for (const p of extra) {
    const k = proposalKey(p);
    if (!seen.has(k)) { seen.add(k); out.push(p); }
  }
  return out;
}
function completenessMissing(proposals, expected) {
  if (!expected || typeof expected !== "number") return 0;
  return Math.max(0, expected - (proposals ? proposals.length : 0));
}

export default function Assistant() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const { user, refreshUser } = useAuth();
  // Resolve the initial role: router state (Home shortcuts) → last chosen role
  // persisted in localStorage (so a hard reload keeps the user in-role) → none.
  const storedMode = typeof window !== "undefined" ? window.localStorage.getItem("chronicles:lastMode") : null;
  const initialMode = resolveInitialMode({ stateMode: location.state?.mode, storedMode });

  const unlocked = aiUnlocked(user);
  const unlimited = isUnlimited(user);

  const [mode, setMode] = useState(initialMode || "assistant");
  // Direct Home shortcuts pass a role → open its chat immediately. The general
  // "Open AI Conversation" button passes nothing → show the role selection first.
  const [roleChosen, setRoleChosen] = useState(!!initialMode);
  const [sessionId, setSessionId] = useState(() => makeSessionId());
  const [messages, setMessages] = useState([]); // {role, content}

  // Persist the active role so a hard reload (or back/forward) keeps the user
  // in-role instead of resetting to the role-select screen.
  useEffect(() => {
    if (roleChosen && mode) {
      try { window.localStorage.setItem("chronicles:lastMode", mode); } catch (e) { /* ignore */ }
    }
  }, [roleChosen, mode]);

  // Single source of truth for choosing/switching a role. Besides updating React
  // state, it writes the mode into react-router's history.state (via replace) so
  // a hard reload AND browser back/forward reconstruct the correct role from the
  // current history entry. localStorage stays as a defensive fallback only.
  const selectMode = (id) => {
    const m = normalizeMode(id) || "assistant";
    setMode(m);
    setRoleChosen(true);
    try { navigate("/assistant", { replace: true, state: { mode: m } }); } catch (e) { /* ignore */ }
  };

  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  // Large AI operations: pendingLarge holds a message awaiting the batch/all choice
  // (shown BEFORE generation); genProgress carries truthful AI-generation progress.
  const [pendingLarge, setPendingLarge] = useState(null);
  const [genProgress, setGenProgress] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [pending, setPending] = useState(() => getPendingProposals());
  const [memBatch, setMemBatch] = useState(null); // { suggestions: [...] }
  const [bulkProposal, setBulkProposal] = useState(null); // pending bulk proposal (pre-approval)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkJobs, setBulkJobs] = useState(() => getBulkJobs());
  const [reviewPkg, setReviewPkg] = useState(() => getActivePackage());
  const [reviewOpen, setReviewOpen] = useState(false);
  const abortRef = useRef(null);
  const scrollerRef = useRef(null);
  // Voice input (OpenAI Whisper). Recording → transcription fills the composer.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Phase-1 proposal DRAFT captured while the AI is still asking for
  // satisfaction. Promoted into the real Proposal Package on confirmation.
  const draftPkgRef = useRef(null);
  // Sticky processing mode for a large plan: once the user chooses batch/all, the
  // choice must survive the two-phase planning turns (the short confirmation turn
  // isn't itself detectable as "large"). Reset when a plan opens/cancels.
  const batchModeRef = useRef(null);

  const modes = useMemo(
    () =>
      MODE_META.map((m) => ({
        ...m,
        label: t(`assistant.modes.${m.id}.label`),
        blurb: t(`assistant.modes.${m.id}.blurb`),
        starter: t(`assistant.modes.${m.id}.starter`),
      })),
    [t]
  );

  useEffect(() => {
    const onChange = () => setPending(getPendingProposals());
    window.addEventListener("chronicles:proposals-changed", onChange);
    return () => window.removeEventListener("chronicles:proposals-changed", onChange);
  }, []);

  // Proposal Review package: restore on load (opens automatically when ready)
  // and keep in sync. Approval happens in the dedicated modal, never in chat.
  useEffect(() => {
    const refresh = () => {
      const pkg = getActivePackage();
      setReviewPkg(pkg);
      if (pkg && pkg.summary && pkg.summary.actionable > 0) setReviewOpen(true);
    };
    window.addEventListener("chronicles:package-changed", refresh);
    refresh();
    return () => window.removeEventListener("chronicles:package-changed", refresh);
  }, []);
  useEffect(() => {
    const refresh = () => setBulkJobs(getBulkJobs());
    window.addEventListener("chronicles:bulkjobs-changed", refresh);
    resumeActiveJobs({ onProgress: refresh });
    refresh();
    return () => window.removeEventListener("chronicles:bulkjobs-changed", refresh);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamBuffer]);

  useEffect(() => {
    if (!streaming) return undefined;
    setElapsed(0);
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [streaming]);

  const activeMode = useMemo(() => modes.find((m) => m.id === mode), [modes, mode]);

  const modeAccess = useMemo(
    () => aiAccess(user, MODE_LEVEL[mode] || "assistant"),
    [user, mode]
  );

  // Build the Proposal Package from a (complete) proposal set and open the
  // dedicated Proposal Review UI. Reuses the existing pipeline/review/executor
  // exactly. Duplicate protection: an identical retry reuses the same slot.
  const openReviewFromProposals = (proposals) => {
    const pkg = runPipeline(proposals, getAllEvents());
    if (!(pkg && pkg.summary.actionable > 0)) return null;
    draftPkgRef.current = null;
    batchModeRef.current = null; // plan finalised → forget the sticky choice
    const cur = getPendingRequest();
    const sig = packageSignature(pkg);
    if (cur && cur.status === REQ_STATUS.READY_FOR_REVIEW && cur.packageSig === sig && getActivePackage()) {
      setReviewOpen(true);
    } else {
      setActivePackage(pkg);
      markReadyForReview(pkg);
      toast.message(t("review.readyTitle"), {
        description: t("review.summaryLine", { count: pkg.summary.actionable }),
      });
    }
    return pkg;
  };

  // Fire one assistant call WITHOUT appending to the visible chat, resolving with
  // the full accumulated text (used for silent completeness continuations).
  const runStreamSilent = (message, history, batchMode = false) =>
    new Promise((resolve) => {
      let acc = "";
      const controller = new AbortController();
      abortRef.current = controller;
      streamAssistant({
        payload: {
          session_id: sessionId, mode, lang, alias: user?.alias || null,
          starters: user?.starters || null, message, history,
          events: getAllEvents(), tasks: getAllTasks(),
          planningMemory: { ...getPlanningMemory(), recentDecisions: getRecentDecisions() }, today: todayISO(),
          batchMode, batchSize: 50,
        },
        signal: controller.signal,
        onDelta: (chunk) => { acc += chunk; setStreamBuffer(acc); },
        onDone: () => resolve({ acc }),
        onError: () => resolve({ acc, error: true }),
      }).catch(() => resolve({ acc, error: true }));
    });

  // COMPLETENESS SELF-CHECK + CONTINUATION: the AI declared `expected` total ops
  // but emitted fewer. Continue generation (asking only for the MISSING ops) and
  // merge, until the authoritative draft is complete or tries are exhausted.
  // Proposal Review is opened ONLY from the completed/merged draft.
  const runCompletion = async (proposals, expected, history, userMsg = "", batchMode = false) => {
    let acc = proposals.slice();
    let tries = 0;
    if (batchMode) setGenProgress({ active: true, ...genBatchInfo(acc.length, expected, 50) });
    while (completenessMissing(acc, expected) > 0 && tries < MAX_COMPLETION_TRIES) {
      tries += 1;
      const missing = completenessMissing(acc, expected);
      const covered = acc.map((p) => {
        const n = p.new || p.old || {};
        const time = n.startHour != null ? ` ${n.startHour}:${String(n.startMinute || 0).padStart(2, "0")}` : "";
        return `${p.kind} ${String(n.title || "").trim()} ${n.date || ""}${time}`.trim();
      });
      const contMsg =
        `CONTINUATION: The agreed plan expects ${expected} operations in total, but only ` +
        `${acc.length} are included so far (${missing} still missing). Emit ONLY the ${missing} ` +
        `missing operations now as a single \`\`\`chronicles-proposals\`\`\` block (same schema, ` +
        `include "expected": ${expected}). Do NOT repeat any of these already-included operations:\n- ` +
        covered.join("\n- ");
      const { acc: raw, error } = await runStreamSilent(contMsg, history, batchMode);
      if (error) break;
      const { proposals: rawMore } = extractProposals(extractBulkPayload(raw).visibleText);
      const more = repairProposalWeekdays(rawMore, userMsg);
      if (!more || !more.length) break;
      const merged = mergeProposals(acc, more);
      if (merged.length === acc.length) break; // no progress
      acc = merged;
      // Truthful AI-generation progress: operations actually produced so far.
      if (batchMode) setGenProgress({ active: true, ...genBatchInfo(acc.length, expected, 50) });
    }
    setStreaming(false);
    setStreamBuffer("");
    setGenProgress(null);
    const pkg = openReviewFromProposals(acc);
    if (!pkg) {
      toast.error(t("review.honestRetry"));
      return;
    }
    if (completenessMissing(acc, expected) > 0) {
      toast.warning(t("review.someMissing", { count: pkg.summary.actionable }));
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    if (!modeAccess.allowed) {
      toast.error(
        modeAccess.reason === "no_credits"
          ? t("assistant.noCreditsTitle")
          : t("assistant.locked.desc")
      );
      return;
    }
    // Large operation? Offer the processing-mode choice BEFORE any AI work.
    // Read-only Statistics never proposes changes, so it is exempt.
    if (mode !== "statistics" && !pendingLarge && batchModeRef.current == null && looksLargeRequest(msg)) {
      setPendingLarge(msg);
      return;
    }
    // Sticky mode: if a large plan is mid-negotiation (two-phase), reuse the
    // user's earlier choice on this (often short) confirmation turn.
    await runSend(msg, batchModeRef.current === true);
  };

  // Fired after the user picks a strategy for a large request.
  const runLargeChoice = (batchMode) => {
    const msg = pendingLarge;
    setPendingLarge(null);
    batchModeRef.current = batchMode; // remember across planning turns
    if (msg) runSend(msg, batchMode);
  };

  const runSend = async (msg, batchMode = false) => {
    setInput("");
    const newHistory = [...messages, { role: "user", content: msg, id: nextMsgId() }];
    setMessages(newHistory);
    setStreaming(true);
    setStreamBuffer("");
    // Batch mode shows truthful AI-generation progress; expected is unknown until
    // the first response returns, so start in an honest indeterminate state.
    setGenProgress(batchMode ? { active: true, ...genBatchInfo(0, null, 50) } : null);

    const controller = new AbortController();
    abortRef.current = controller;

    let acc = "";
    await streamAssistant({
      payload: {
        session_id: sessionId,
        mode,
        lang,
        alias: user?.alias || null,
        starters: user?.starters || null,
        message: msg,
        history: messages,
        events: getAllEvents(),
        tasks: getAllTasks(),
        planningMemory: { ...getPlanningMemory(), recentDecisions: getRecentDecisions() },
        today: todayISO(),
        batchMode,
        batchSize: 50,
      },
      signal: controller.signal,
      onDelta: (chunk) => {
        acc += chunk;
        setStreamBuffer(acc);
      },
      onDone: () => {
        // Weekday intent may live in an earlier planning turn (Two-Phase flow),
        // so give the guard the recent user-message context, not just this turn.
        const planCtx = [...messages, { role: "user", content: msg }]
          .filter((m) => m.role === "user").slice(-6).map((m) => m.content).join("  ");
        const { visibleText: vb, bulk } = extractBulkPayload(acc);
        const parsed = extractProposals(vb);
        const { visibleText: v1, expected } = parsed;
        // Deterministic weekday guard: repair a uniform off-by-one against the
        // weekday(s) the user named (app supplies calendar truth — Package E).
        const proposals = repairProposalWeekdays(parsed.proposals, planCtx);
        const { visibleText, suggestions } = extractMemorySuggestions(v1);
        // Statistics is strictly read-only: never surface proposals, bulk plans
        // or memory suggestions from it, even if the model hallucinates a block.
        const readOnly = mode === "statistics";
        // Run the proposal pipeline BEFORE composing the chat message so we can
        // show a clean natural-language summary and keep JSON out of the chat.
        let pkg = null;
        if (!readOnly && proposals && proposals.length) {
          pkg = runPipeline(proposals, getAllEvents());
        }
        // Chat bubble: hardened natural-language only (never proposal JSON). If
        // the model returned no prose but did produce changes, synthesize a
        // clean summary line instead.
        let chatText = scrubProposalArtifacts(visibleText);
        if (!chatText && pkg && pkg.summary.actionable > 0) {
          chatText = t("review.chatReady", { count: pkg.summary.actionable });
        }
        const hasPkg = !!(pkg && pkg.summary.actionable > 0);
        const asksQuestion = /\?/.test(chatText);
        const producedChanges = hasPkg || !!bulk;

        // --- Satisfaction → Proposal Review transition ----------------------
        // FINALISE (Phase 3): a concrete proposal block arrived → open Review.
        //   Per the backend workflow, emitting the block IS the finalise step, so
        //   the review must open even if the model appended a trailing courtesy
        //   question ("…anything else?"). A stray "?" must NOT swallow a finalised
        //   package into a draft (root cause of Review never appearing).
        // Confirmation with no fresh block ("Opening the review…"): the AI already
        //   emitted the block earlier and only acknowledged now → promote the
        //   captured draft into the real package and open Review.
        // Phase 1 (planning): no block emitted (hasPkg false) → nothing opens; the
        //   AI keeps negotiating until it finalises with a block.
        // openSet carries the raw proposals + declared `expected` total so the
        // completeness self-check can run before Review is opened.
        let openSet = null;
        if (!readOnly && hasPkg) {
          openSet = { proposals, expected };
        } else if (!readOnly && draftPkgRef.current) {
          openSet = draftPkgRef.current;
        }

        // Truthfulness: the AI claims success but there is nothing to open and no
        // draft to promote → keep the pending request and show an honest retry.
        // Also covers the silent case where a chronicles-* fence STARTED but was
        // so truncated that not even one object could be salvaged (no package, no
        // bulk, no draft) — never leave the user staring at a "done" sentence with
        // nothing applied.
        const hadFenceStart = /```chronicles-(proposals|bulk)/.test(acc);
        const falseSuccess =
          !readOnly && !producedChanges && !openSet && !asksQuestion &&
          (claimsProposalSuccess(chatText) || hadFenceStart);
        if (falseSuccess) {
          markFailedPartial("no_package_generated");
          chatText = t("review.honestRetry");
          setGenProgress(null);
        }
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: chatText, id: nextMsgId() },
        ]);
        setStreamBuffer("");
        setStreaming(false);
        if (!unlimited) refreshUser();
        if (!readOnly && suggestions && suggestions.length) {
          setMemBatch({ suggestions });
        }
        if (!readOnly && bulk) {
          setGenProgress(null);
          batchModeRef.current = null; // bulk plan finalised → forget sticky mode
          const bp = buildBulkProposal(bulk, getAllEvents());
          setBulkProposal(bp);
          toast.message(t("bulk.proposalReadyTitle"), {
            description: t("bulk.proposalReadyDesc", { count: bp.estimatedEventCount }),
          });
        }
        if (openSet) {
          draftPkgRef.current = null;
          // COMPLETENESS: before opening Review, ensure every declared operation
          // is present. If the AI under-generated, continue silently to fill the
          // gap, THEN open Review from the completed draft.
          if (completenessMissing(openSet.proposals, openSet.expected) > 0) {
            setStreaming(true);
            // Truthful progress from the first response's real op count.
            if (batchMode) setGenProgress({ active: true, ...genBatchInfo(openSet.proposals.length, openSet.expected, 50) });
            const history = [
              ...messages,
              { role: "user", content: msg },
              { role: "assistant", content: chatText },
            ];
            runCompletion(openSet.proposals, openSet.expected, history, planCtx, batchMode);
          } else {
            setGenProgress(null);
            openReviewFromProposals(openSet.proposals);
          }
        } else if (!falseSuccess) {
          setGenProgress(null);
        }
      },
      onError: (err) => {
        setStreaming(false);
        setStreamBuffer("");
        setGenProgress(null);
        batchModeRef.current = null;
        toast.error(err.message || t("assistant.errorGeneric"));
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: t("assistant.errorReach"),
            id: nextMsgId(),
          },
        ]);
      },
    });
  };

  const onStop = () => {
    abortRef.current?.abort();
    setStreaming(false);
    setGenProgress(null);
    batchModeRef.current = null;
  };

  // --- Voice input handlers -------------------------------------------------
  const startRecording = async () => {
    if (recording || transcribing || streaming) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices || typeof MediaRecorder === "undefined") {
      toast.error(t("assistant.voice.unsupported"));
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error(t("assistant.voice.denied"));
      return;
    }
    const mr = new MediaRecorder(stream);
    audioChunksRef.current = [];
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size) audioChunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      stream.getTracks().forEach((tr) => tr.stop());
      const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
      audioChunksRef.current = [];
      if (!blob.size) return;
      setTranscribing(true);
      try {
        const text = await transcribeAudio(blob, lang);
        if (text) {
          setInput((prev) => (prev ? `${prev} ${text}` : text));
        } else {
          toast.message(t("assistant.voice.empty"));
        }
      } catch (err) {
        toast.error(err.message || t("assistant.voice.error"));
      } finally {
        setTranscribing(false);
      }
    };
    mr.start();
    mediaRecorderRef.current = mr;
    setRecording(true);
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && recording) {
      mr.stop();
      setRecording(false);
    }
  };

  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  const onReset = () => {
    if (streaming) abortRef.current?.abort();
    draftPkgRef.current = null;
    setMessages([]);
    setStreamBuffer("");
    setSessionId(makeSessionId());
  };

  const onApply = (batch) => {
    removeProposalBatch(batch.id);
    toast.success(t("assistant.changesApplied"));
  };
  const onReject = (batch) => {
    removeProposalBatch(batch.id);
    toast(t("assistant.proposalDismissed"));
  };

  // --- Bulk proposal / job handlers ---
  const refreshBulkJobs = () => setBulkJobs(getBulkJobs());

  const onBulkApproveAll = async () => {
    if (!bulkProposal || bulkBusy) return;
    setBulkBusy(true);
    const res = createBulkJob(bulkProposal);
    if (!res.ok) {
      toast.error(`${t("bulk.createErrorGeneric")}: ${res.reason}`);
      setBulkBusy(false);
      return;
    }
    setBulkProposal(null);
    refreshBulkJobs();
    toast.success(t("bulk.jobStarted"));
    try {
      await runBulkJob(res.jobId, { onProgress: refreshBulkJobs });
    } finally {
      setBulkBusy(false);
      refreshBulkJobs();
    }
  };

  const onBulkReject = () => {
    setBulkProposal(null);
    toast(t("bulk.proposalDismissed"));
  };

  const onBulkCancel = (job) => { cancelBulkJob(job.jobId); refreshBulkJobs(); };
  const onBulkRetry = async (job) => { await retryFailedItems(job.jobId, { onProgress: refreshBulkJobs }); refreshBulkJobs(); };
  const onBulkRollback = (job) => { rollbackBulkJob(job.jobId); refreshBulkJobs(); toast(t("bulk.rolledBackToast")); };
  const onBulkDismiss = (job) => { removeBulkJob(job.jobId); refreshBulkJobs(); };

  // Proposal Review handlers
  const onReviewClose = () => setReviewOpen(false);
  const onReviewDone = (res) => {
    clearActivePackage();
    setReviewPkg(null);
    setReviewOpen(false);
    // Recent Decision Memory: remember the last few accepted/rejected suggestions
    // (short-term only; capped at 5 in planningMemory).
    const decisions = [
      ...((res && res.accepted) || []).map((signature) => ({ action: "accepted", signature })),
      ...((res && res.rejected) || []).map((signature) => ({ action: "rejected", signature })),
    ];
    if (decisions.length) recordDecisions(decisions);
    if (res && res.rejectedAll) {
      clearPendingRequest();
      toast(t("review.rejectedAllToast"));
    } else {
      // Applied: mark the planning request APPLIED and clear it (prevents any
      // duplicate execution of the same package).
      markApplied();
    }
  };

  const onResolveMemory = (idx) => {
    setMemBatch((prev) => {
      if (!prev) return null;
      const next = prev.suggestions.filter((_, i) => i !== idx);
      return next.length ? { suggestions: next } : null;
    });
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col" data-testid="assistant-locked">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-card/80 border-b border-border">
          <div className="mx-auto max-w-3xl px-4 sm:px-5 py-3 flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100"
              data-testid="assistant-back"
              aria-label={t("assistant.backToHome")}
            >
              <ArrowLeft className="h-4 w-4 text-slate-700" />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{t("assistant.headerOverline")}</p>
              <p className="font-display text-base font-semibold text-slate-900 truncate">{t("assistant.headerTitle")}</p>
            </div>
            <LanguageSwitcher />
          </div>
        </header>
        <main className="flex-1 mx-auto w-full max-w-2xl px-5 py-12 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 flex items-center justify-center mb-5">
            <Lock className="h-6 w-6 text-white" strokeWidth={2.2} />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("assistant.locked.title")}</h1>
          <p className="mt-2 text-sm text-slate-600">{t("assistant.locked.desc")}</p>
          <Button
            className="mt-7 rounded-full h-12 px-6 bg-slate-900 hover:bg-slate-800"
            onClick={() => navigate("/settings")}
            data-testid="assistant-upgrade-cta"
          >
            {t("assistant.locked.cta")}
          </Button>
          <button
            className="mt-3 text-sm text-slate-500 hover:text-slate-900 underline"
            onClick={() => navigate("/settings")}
            data-testid="assistant-have-code"
          >
            {t("assistant.locked.haveCode")}
          </button>

          <div className="mt-10 w-full">
            <PlanComparison currentPlan={planOf(user)} />
          </div>
        </main>
      </div>
    );
  }

  const creditsLabel =
    unlimited || modeAccess.credits === "unlimited"
      ? t("assistant.unlimited")
      : modeAccess.max
      ? `${modeAccess.credits} / ${modeAccess.max}`
      : t("assistant.creditsLeft", { count: modeAccess.credits });

  // Role selection screen — shown only when opened via the general
  // "Open AI Conversation" button (no role preselected).
  if (!roleChosen) {
    return (
      <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-card/80 border-b border-border">
          <div className="mx-auto max-w-3xl px-4 sm:px-5 py-3 flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100"
              data-testid="assistant-back"
              aria-label={t("assistant.backToHome")}
            >
              <ArrowLeft className="h-4 w-4 text-slate-700" />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{t("assistant.headerOverline")}</p>
              <p className="font-display text-base font-semibold text-slate-900 truncate">{t("assistant.headerTitle")}</p>
            </div>
            <LanguageSwitcher />
          </div>
        </header>
        <main className="flex-1 mx-auto w-full max-w-2xl px-4 sm:px-5 py-8" data-testid="assistant-role-select">
          <h1 className="font-display text-2xl font-semibold text-slate-900">{t("assistant.chooseRoleTitle")}</h1>
          <p className="text-sm text-slate-600 mt-1.5 mb-6">{t("assistant.chooseRoleDesc")}</p>
          <div className="grid gap-3">
            {modes.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => {
                    selectMode(m.id);
                  }}
                  className="btn-lift text-left flex items-start gap-3 rounded-[20px] border border-border bg-card px-4 py-4 hover:bg-accent transition shadow-sm"
                  data-testid={`assistant-role-${m.id}`}
                >
                  <span className="mt-0.5 h-9 w-9 shrink-0 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-base font-semibold text-slate-900">{m.label}</span>
                    <span className="block text-sm text-slate-600 mt-0.5">{m.blurb}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-5 py-3 flex items-center gap-2">
          <Link
            to="/"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100"
            data-testid="assistant-back"
            aria-label={t("assistant.backToHome")}
          >
            <ArrowLeft className="h-4 w-4 text-slate-700" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{t("assistant.headerOverline")}</p>
            <p className="font-display text-base font-semibold text-slate-900 truncate">
              {t("assistant.headerTitle")}
            </p>
          </div>
          <span
            className="hidden sm:inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium px-2.5 py-1"
            data-testid="assistant-credits"
          >
            {creditsLabel}
          </span>
          <LanguageSwitcher />
          <button
            onClick={onReset}
            className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100"
            data-testid="assistant-new-chat"
          >
            {t("assistant.newChat")}
          </button>
        </div>

        {/* Mode tabs */}
        <div className="mx-auto max-w-3xl px-4 sm:px-5 pb-3 flex gap-2 overflow-x-auto" data-testid="assistant-mode-tabs">
          {modes.map((m) => {
            const Icon = m.icon;
            const active = m.id === mode;
            return (
              <button
                key={m.id}
                onClick={() => selectMode(m.id)}
                className={[
                  "shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-sm transition border",
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-card text-slate-700 border-slate-300 hover:bg-accent",
                ].join(" ")}
                data-testid={`assistant-mode-${m.id}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {m.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Pending proposals — never shown in read-only Statistics mode */}
      {mode !== "statistics" && pending.length > 0 ? (
        <section className="mx-auto w-full max-w-3xl px-4 sm:px-5 pt-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">
            {t("assistant.pendingProposals")}
          </p>
          <div className="space-y-3" data-testid="assistant-pending-list">
            {pending.map((batch) => (
              <ProposalCard
                key={batch.id}
                batch={batch}
                onApplied={() => onApply(batch)}
                onRejected={() => onReject(batch)}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Proposal Review reopen banner (approval lives in the dedicated modal) */}
      {mode !== "statistics" && reviewPkg && reviewPkg.summary?.actionable > 0 && !reviewOpen ? (
        <section className="mx-auto w-full max-w-3xl px-4 sm:px-5 pt-4">
          <button
            onClick={() => setReviewOpen(true)}
            className="w-full rounded-2xl border border-border bg-card px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition text-left"
            data-testid="review-reopen"
          >
            <span className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">{reviewPkg.summary.actionable}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-900">{t("review.readyTitle")}</span>
              <span className="block text-xs text-slate-500">{t("review.summaryLine", { count: reviewPkg.summary.actionable })}</span>
            </span>
            <span className="text-xs font-medium text-slate-700">{t("review.open")}</span>
          </button>
        </section>
      ) : null}

      {/* Bulk calendar plan (pre-approval) and active/finished bulk jobs */}
      {mode !== "statistics" && bulkProposal ? (
        <section className="mx-auto w-full max-w-3xl px-4 sm:px-5 pt-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{t("bulk.bulkPlan")}</p>
          <BulkProposalCard
            proposal={bulkProposal}
            busy={bulkBusy}
            onApproveAll={onBulkApproveAll}
            onReject={onBulkReject}
          />
        </section>
      ) : null}

      {bulkJobs.length > 0 ? (
        <section className="mx-auto w-full max-w-3xl px-4 sm:px-5 pt-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{t("bulk.jobsTitle")}</p>
          <div className="space-y-3" data-testid="assistant-bulk-jobs">
            {bulkJobs.map((job) => (
              <BulkJobProgress
                key={job.jobId}
                job={job}
                onCancel={onBulkCancel}
                onRetry={onBulkRetry}
                onRollback={onBulkRollback}
                onDismiss={onBulkDismiss}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Planning-memory suggestions (user-approved) */}
      {memBatch ? (
        <section className="mx-auto w-full max-w-3xl px-4 sm:px-5 pt-4">
          <MemorySuggestionCard batch={memBatch} onResolved={onResolveMemory} />
        </section>
      ) : null}

      {/* Chat scroller */}
      <main
        ref={scrollerRef}
        className="flex-1 mx-auto w-full max-w-3xl px-4 sm:px-5 py-5 space-y-4 overflow-y-auto pb-40"
        data-testid="assistant-chat"
      >
        {messages.length === 0 && !streamBuffer ? (
          <div className="flex flex-col items-center justify-center text-center py-16" data-testid="assistant-empty-state">
            <div className="h-12 w-12 rounded-2xl bg-slate-900 flex items-center justify-center mb-4">
              <activeMode.icon className="h-5 w-5 text-white" />
            </div>
            <h2 className="font-display text-lg font-semibold text-slate-900">{activeMode.label}</h2>
            <p className="text-sm text-slate-500 mt-1.5 max-w-sm">{activeMode.blurb}</p>
          </div>
        ) : null}

        {messages.map((m) => (
          <Bubble key={m.id} role={m.role} text={m.content} />
        ))}
        {(() => {
          const visibleStream = stripStreamingArtifacts(streamBuffer);
          return (
            <>
              {streaming && !visibleStream ? (
                <div className="flex w-full justify-start" data-testid="assistant-thinking">
                  <div className="bg-card border border-border rounded-[20px] rounded-bl-md px-4 py-3 shadow-sm flex items-center gap-2 text-slate-500">
                    <Hourglass className="h-4 w-4 animate-hourglass" />
                    <span className="text-sm">{t("assistant.thinking")}</span>
                    <span className="text-sm font-mono-tab text-slate-400" data-testid="assistant-elapsed">{elapsed}s</span>
                  </div>
                </div>
              ) : null}
              {streaming && visibleStream ? (
                <Bubble role="assistant" text={visibleStream} streaming />
              ) : null}
            </>
          );
        })()}
      </main>

      {/* Composer */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-card/95 backdrop-blur-xl border-t border-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-5 py-3 pb-3 sm:pb-3">
          {!modeAccess.allowed ? (
            <div
              className="mb-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 flex items-center gap-2"
              data-testid="assistant-no-credits-banner"
            >
              <Lock className="h-3.5 w-3.5 text-amber-700 shrink-0" />
              <p className="text-xs text-amber-800">
                {mode === "strategy" ? t("assistant.advisorNoCredits") : t("assistant.noCreditsDesc")}
              </p>
            </div>
          ) : null}
          {mode === "strategy" && messages.length > 0 ? (
            <div
              className="flex gap-2 overflow-x-auto pb-2.5"
              data-testid="advisor-quick-toolbar"
            >
              {ADVISOR_QUICK_CHIP_INDEXES.map((idx) => {
                const chip = t("assistant.advisorChips")[idx];
                return (
                  <button
                    key={`chip-${idx}`}
                    onClick={() => send(chip)}
                    disabled={streaming}
                    className="shrink-0 rounded-full border border-border bg-card px-3 h-8 text-xs text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition disabled:opacity-50"
                    data-testid={`advisor-quick-${idx}`}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>
          ) : null}
          {pendingLarge ? (
            <div className="mb-2 rounded-2xl border border-border bg-card p-3 space-y-2" data-testid="ai-large-choice">
              <p className="text-sm font-semibold text-slate-900">{t("assistant.large.title")}</p>
              <p className="text-xs text-slate-500 -mt-1">{t("assistant.large.subtitle")}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => runLargeChoice(true)}
                  className="flex-1 text-left rounded-xl border border-slate-900 bg-white hover:bg-slate-50 px-4 py-3 transition"
                  data-testid="ai-choice-batch"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-900">{t("assistant.large.batch")}</span>
                    <span className="text-[10px] uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5">{t("assistant.large.recommended")}</span>
                  </div>
                </button>
                <button
                  onClick={() => runLargeChoice(false)}
                  className="flex-1 text-left rounded-xl border border-border bg-white hover:bg-slate-50 px-4 py-3 transition"
                  data-testid="ai-choice-all"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-900">{t("assistant.large.all")}</span>
                    <span className="text-[10px] uppercase tracking-wide rounded-full bg-slate-200 text-slate-600 px-2 py-0.5">{t("assistant.large.slower")}</span>
                  </div>
                </button>
              </div>
              <button onClick={() => setPendingLarge(null)} className="text-xs text-slate-500 hover:text-slate-800 underline" data-testid="ai-choice-cancel">
                {t("assistant.large.cancel")}
              </button>
            </div>
          ) : null}

          {genProgress?.active ? (
            <div className="mb-2 rounded-2xl border border-border bg-card p-3 space-y-1.5" data-testid="ai-gen-progress">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{t("assistant.large.generating")}</span>
                <span className="text-xs text-slate-500 font-mono-tab" data-testid="ai-gen-count">
                  {genProgress.expected
                    ? t("assistant.large.genCount", { done: genProgress.generated, total: genProgress.expected })
                    : t("assistant.large.working")}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                <div
                  className={["h-full rounded-full bg-slate-900 transition-all duration-300", genProgress.pct == null ? "animate-pulse w-1/3" : ""].join(" ")}
                  style={genProgress.pct == null ? undefined : { width: `${genProgress.pct}%` }}
                  data-testid="ai-gen-bar"
                />
              </div>
              {genProgress.batch != null ? (
                <p className="text-xs text-slate-500" data-testid="ai-gen-stage">
                  {t("assistant.large.batchOf", { current: genProgress.batch, total: genProgress.totalBatches })}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-end gap-2 pr-0 sm:pr-0" style={{ paddingRight: "0" }}>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t(`assistant.modes.${mode}.placeholder`)}
              rows={1}
              disabled={!modeAccess.allowed}
              className="rounded-2xl min-h-12 resize-none flex-1 disabled:opacity-60"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              data-testid="assistant-input"
            />
            {modeAccess.allowed ? (
              <Button
                type="button"
                variant="outline"
                onClick={toggleRecording}
                disabled={streaming || transcribing}
                className={[
                  "rounded-full h-12 w-12 p-0 shrink-0 transition",
                  recording
                    ? "bg-rose-600 text-white border-rose-600 hover:bg-rose-700 animate-pulse"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100",
                ].join(" ")}
                aria-label={recording ? t("assistant.voice.stop") : t("assistant.voice.record")}
                title={recording ? t("assistant.voice.stop") : t("assistant.voice.record")}
                data-testid="assistant-mic"
              >
                {transcribing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            ) : null}
            {streaming ? (
              <Button
                variant="outline"
                className="rounded-full h-12 px-4 shrink-0"
                onClick={onStop}
                data-testid="assistant-stop"
              >
                <Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" />
                <span className="hidden sm:inline">{t("assistant.stop")}</span>
              </Button>
            ) : (
              <Button
                className="rounded-full h-12 px-5 bg-slate-900 hover:bg-slate-800 shrink-0"
                onClick={() => send()}
                disabled={!input.trim() || !modeAccess.allowed}
                data-testid="assistant-send"
              >
                <Send className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{t("assistant.send")}</span>
              </Button>
            )}
          </div>
          {/* Spacer for the Emergent badge on mobile */}
          <div className="h-10 sm:h-0" aria-hidden />
        </div>
      </div>

      {/* Dedicated Proposal Review — opens outside the chat */}
      {reviewOpen && reviewPkg ? (
        <ProposalReview pkg={reviewPkg} onClose={onReviewClose} onDone={onReviewDone} />
      ) : null}
    </div>
  );
}

function Bubble({ role, text, streaming }) {
  const isUser = role === "user";
  return (
    <div
      className={["flex w-full", isUser ? "justify-end" : "justify-start"].join(" ")}
      data-testid={`assistant-bubble-${role}`}
    >
      <div
        className={[
          "max-w-[85%] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed shadow-sm",
          isUser
            ? "bg-primary text-primary-foreground rounded-[20px] rounded-br-md"
            : "bg-card border border-border text-slate-800 rounded-[20px] rounded-bl-md",
        ].join(" ")}
      >
        {text}
        {streaming ? <span className="inline-block w-2 h-4 bg-slate-400 ml-1 align-middle animate-pulse" /> : null}
      </div>
    </div>
  );
}
